import { ConnectionStatusIndicator } from '@/components/workflow/ConnectionStatusIndicator';
import { ThemeToggle } from '@/components/workflow/ThemeToggle';
import { EngiCoroLogo } from '@/components/workflow/EngiCoroLogo';
import { LanguageSelector } from '@/components/workflow/LanguageSelector';
import type { ConnectionDebugInfo } from '@/hooks/useDocuments';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { ProfileMenu } from '@/components/shell/ProfileMenu';
import { TopBarMobileMenu } from '@/gui/workflow/TopBarMobileMenu';
import { TopBarDesktopActions } from '@/gui/workflow/TopBarDesktopActions';
import { CoopPresenceBadge } from '@/components/workflow/CoopPresenceBadge';
import { AppNav } from '@/components/shell/AppNav';
import { BRAND_NAVY, BRAND_SIDEBAR } from '@/constants/brandColors';

interface TopBarProps {
  onSave: () => void;
  onSaveAs: () => void;
  /**
   * Spara/uppdatera workflow till molnet. Öppnar SaveDialog i UPDATE-läge
   * när currentDocumentId !== null så user kan uppdatera existerande
   * cloud-kopia, annars skapar ny.
   */
  onSaveToCloud?: () => void;
  /** True när workflow:et redan är sparat i molnet — visar "Uppdatera"-text. */
  hasCloudDocument?: boolean;
  /** True när authenticerad — döljer cloud-knappen för anon-users. */
  showCloudActions?: boolean;
  /** Öppna NewWorkflowDialog för att skapa nytt flöde med type-val. */
  onNewFlow?: () => void;
  onLoad: () => void;
  onRepoUpload?: () => void;
  onExportPNG: () => void;
  onSimulate: () => void;
  onClear: () => void;
  onImportJson: () => void;
  onCopyJson: () => void;
  onHelp: () => void;
  onAiJson: () => void;
  onAiAssistant: () => void;
  onPreview: () => void;
  onLogin?: () => void;
  onCloudWorkspace?: () => void;
  onShareWorkspace?: () => void;
  /** Öppna Process-motor-dialog (Enterprise-only, se ProcessMotorDialog). */
  onProcessMotor?: () => void;
  /** Öppna "What are you building?"-wizard:n igen (re-seed för aktuell sheet). */
  onReopenWizard?: () => void;
  onFeatureRequest?: () => void;
  onBugReport?: () => void;
  onDonate?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  isConnected?: boolean;
  connectionDebugInfo?: ConnectionDebugInfo;
  currentDocumentTitle?: string;
  hasLoadedDocument?: boolean;
  isDirty?: boolean;
  coopActive?: boolean;
  coopCollaborators?: string[];
}

export function TopBar(props: TopBarProps) {
  const {
    onSave, onSaveToCloud, hasCloudDocument, onNewFlow, onLoad, onRepoUpload, onExportPNG, onSimulate, onClear,
    onImportJson, onCopyJson, onHelp, onAiJson, onAiAssistant, onPreview,
    onLogin, onCloudWorkspace, onShareWorkspace, onProcessMotor, onReopenWizard, onFeatureRequest, onBugReport, onDonate,
    onUndo, onRedo, canUndo, canRedo,
    isConnected, connectionDebugInfo,
    currentDocumentTitle, isDirty,
    coopActive, coopCollaborators,
  } = props;

  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();

  return (
    <div className="flex flex-col" data-testid="topbar-stack">
      {/* Row 1 — page-level nav (Home / Flow-weaver / Process Engine /
          Atlas / Final). Tier-gated. Compact strip above the toolbar. */}
      <div
        className="flex items-center px-3 sm:px-5 py-1 border-b border-black/5"
        style={{ background: '#dfe2e6' }}
        data-testid="topbar-nav-row"
      >
        <AppNav />
      </div>
    <header
      className="h-20 flex items-center justify-between px-3 sm:px-5 shadow-lg"
      style={{ background: BRAND_SIDEBAR }}
      role="banner"
      data-guide="topbar"
      data-testid="topbar"
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <EngiCoroLogo className={isMobile ? 'h-14 flex-shrink-0' : 'h-16 flex-shrink-0'} />
        {currentDocumentTitle && !isMobile && (
          <span className="text-sm ml-2 truncate max-w-[200px]" style={{ color: BRAND_NAVY, opacity: 0.7 }}>
            — {currentDocumentTitle}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-2">
        <div className="flex items-center gap-1.5 mr-1 sm:mr-2">
          <ConnectionStatusIndicator isConnected={!!isConnected} debugInfo={connectionDebugInfo} />
          {isDirty && (
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" title="Unsaved changes" />
          )}
          {coopActive && (
            <CoopPresenceBadge collaborators={coopCollaborators ?? []} />
          )}
        </div>

        {isMobile ? (
          <TopBarMobileMenu
            isAuthenticated={isAuthenticated}
            canUndo={canUndo} canRedo={canRedo}
            onSave={onSave} onLoad={onLoad} onRepoUpload={onRepoUpload} onExportPNG={onExportPNG}
            onSimulate={onSimulate} onClear={onClear}
            onImportJson={onImportJson} onCopyJson={onCopyJson}
            onHelp={onHelp} onAiJson={onAiJson} onAiAssistant={onAiAssistant}
            onPreview={onPreview} onLogin={onLogin}
            onCloudWorkspace={onCloudWorkspace} onShareWorkspace={onShareWorkspace}
            onFeatureRequest={onFeatureRequest} onBugReport={onBugReport} onDonate={onDonate}
            onUndo={onUndo} onRedo={onRedo}
            onReopenWizard={onReopenWizard}
          />
        ) : (
          <TopBarDesktopActions
            isAuthenticated={isAuthenticated}
            canUndo={canUndo} canRedo={canRedo}
            onSave={onSave}
            onSaveToCloud={onSaveToCloud}
            hasCloudDocument={hasCloudDocument}
            onNewFlow={onNewFlow}
            onLoad={onLoad} onRepoUpload={onRepoUpload} onExportPNG={onExportPNG}
            onImportJson={onImportJson} onCopyJson={onCopyJson}
            onHelp={onHelp} onAiJson={onAiJson} onAiAssistant={onAiAssistant}
            onSimulate={onSimulate} onPreview={onPreview}
            onCloudWorkspace={onCloudWorkspace} onShareWorkspace={onShareWorkspace}
            onProcessMotor={onProcessMotor}
            onReopenWizard={onReopenWizard}
            onFeatureRequest={onFeatureRequest} onBugReport={onBugReport} onDonate={onDonate}
            onClear={onClear} onUndo={onUndo} onRedo={onRedo}
          />
        )}

        {/* Single consolidated dropdown — holds email / realtime-sync /
            admin-dashboard / preferences / restart-onboarding / sign-out.
            TopBarUserMenu has been merged into ProfileMenu. */}
        <ProfileMenu onLogin={onLogin} />
        <ThemeToggle className="text-brand-navy hover:bg-brand-navy/10" />
        <LanguageSelector className="text-brand-navy hover:bg-brand-navy/10" />
      </div>
    </header>
    </div>
  );
}
